import { Avatar } from '@dixa/lib-shared-habla';
import { animated, config, useSpring, useTrail } from '@react-spring/web';
import { CSSProperties, memo, useEffect, useMemo, useState } from 'react';

import { rewindConfig } from 'components/rewind';

import { useRewindContext } from '../../../rewindModal.context';
import { RewindSlideProps } from '../../../rewindModal.types';
import { SlideContainer } from '../../slideContainer';

import { DiscoBall } from './components/discoBall/discoBall';
import styles from './openingSlide.module.scss';

// Animation phase timings
const DISCO_PHASE_DURATION = 4300; // Disco balls spin in center
const AVATAR_REVEAL_DURATION = 1300; // Transform to avatar
const GREETING_DURATION = 1500; // Show greeting
const CONTENT_ANIMATION_DURATION = 7500; // Rest of content
const LOADING_MESSAGE_DELAY = 3000; // Delay after content finishes before showing loading message

type OpeningSlideProps = RewindSlideProps & {
  isLoading?: boolean;
};

export const OpeningSlide = memo(function OpeningSlide({
  isCompleted,
  isLoading = false,
}: OpeningSlideProps): JSX.Element {
  const { user } = useRewindContext();

  const [phase, setPhase] = useState<
    'disco' | 'avatar' | 'greeting' | 'content' | 'complete'
  >(isCompleted ? 'complete' : 'disco');

  // Track if we've reached the loading message phase (separate from content phase)
  const [isLoadingMessagePhase, setIsLoadingMessagePhase] = useState(false);

  useEffect(() => {
    // If slide has completed before, skip animations
    if (isCompleted) {
      setPhase('complete');
      return;
    }

    // Phase 1: Disco balls spinning
    const avatarTimer = setTimeout(() => {
      setPhase('avatar');
    }, DISCO_PHASE_DURATION);

    // Phase 2: Avatar revealed
    const greetingTimer = setTimeout(() => {
      setPhase('greeting');
    }, DISCO_PHASE_DURATION + AVATAR_REVEAL_DURATION);

    // Phase 3: Greeting shown
    const contentTimer = setTimeout(
      () => {
        setPhase('content');
      },
      DISCO_PHASE_DURATION + AVATAR_REVEAL_DURATION + GREETING_DURATION,
    );

    // Loading message phase - fires 3s after content animation completes
    const loadingMessageTimer = setTimeout(
      () => {
        setIsLoadingMessagePhase(true);
      },
      DISCO_PHASE_DURATION +
        AVATAR_REVEAL_DURATION +
        GREETING_DURATION +
        CONTENT_ANIMATION_DURATION +
        LOADING_MESSAGE_DELAY,
    );

    // Phase 4: All content shown
    const completeTimer = setTimeout(
      () => {
        setPhase('complete');
      },
      DISCO_PHASE_DURATION +
        AVATAR_REVEAL_DURATION +
        GREETING_DURATION +
        CONTENT_ANIMATION_DURATION,
    );

    return () => {
      clearTimeout(avatarTimer);
      clearTimeout(greetingTimer);
      clearTimeout(contentTimer);
      clearTimeout(loadingMessageTimer);
      clearTimeout(completeTimer);
    };
  }, [isCompleted]);

  const showContent = phase === 'content' || phase === 'complete';
  const showGreeting = phase === 'greeting' || showContent;
  const showAvatar = phase === 'avatar' || showGreeting;
  const showDisco = phase === 'disco' || phase === 'avatar';

  // Generate random sparkles for the disco scene - skip if already completed
  const sparkles = useMemo(() => {
    if (isCompleted) return [];
    const colors = [
      'rgba(255, 0, 255, 0.3)', // magenta
      'rgba(0, 255, 255, 0.3)', // cyan
      'rgba(255, 255, 255, 0.4)', // white
      'rgba(255, 100, 255, 0.25)', // light magenta
      'rgba(100, 255, 255, 0.25)', // light cyan
      'rgba(255, 200, 100, 0.2)', // warm
    ];
    return Array.from({ length: 18 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      size: 4 + Math.random() * 14, // Size for the ray length (4-18px)
      delay: Math.random() * 3,
      duration: 1.5 + Math.random() * 2,
      color: colors[Math.floor(Math.random() * colors.length)],
    }));
  }, [isCompleted]);

  // Avatar container animation - starts centered, moves up and scales down when content appears
  const avatarContainerSpring = useSpring({
    transform: showContent
      ? 'translate(-50%, -50%) translateY(-280px) scale(0.4)'
      : 'translate(-50%, -50%) translateY(0px) scale(1)',
    config: { tension: 120, friction: 14 },
    immediate: isCompleted,
  });

  // Avatar circle animation - appears from disco ball
  const avatarSpring = useSpring({
    opacity: showAvatar ? 1 : 0,
    transform: showAvatar
      ? 'scale(1) rotateY(0deg)'
      : 'scale(0.3) rotateY(180deg)',
    config: { tension: 200, friction: 20 },
    immediate: isCompleted,
  });

  // Greeting text animation
  const greetingSpring = useSpring({
    opacity: showGreeting ? 1 : 0,
    transform: showGreeting ? 'translateY(0px)' : 'translateY(20px)',
    config: { tension: 280, friction: 20 },
    delay: showGreeting && !isCompleted ? 200 : 0,
    immediate: isCompleted,
  });

  // Year badge animation - drops in with bounce
  const yearBadgeSpring = useSpring({
    from: { opacity: 0, transform: 'scale(0.3) translateY(-50px)' },
    to: showContent
      ? { opacity: 1, transform: 'scale(1) translateY(0px)' }
      : { opacity: 0, transform: 'scale(0.3) translateY(-50px)' },
    delay: showContent && !isCompleted ? 400 : 0,
    config: { ...config.wobbly, tension: 200, friction: 12 },
    immediate: isCompleted,
  });

  // "Dixa" text animation - slides in from left
  const dixaSpring = useSpring({
    from: { opacity: 0, transform: 'translateX(-150px) scale(0.8)' },
    to: showContent
      ? { opacity: 1, transform: 'translateX(0px) scale(1)' }
      : { opacity: 0, transform: 'translateX(-150px) scale(0.8)' },
    delay: showContent && !isCompleted ? 1200 : 0,
    config: { ...config.gentle, tension: 100, friction: 14 },
    immediate: isCompleted,
  });

  // "Rewind" text animation - slides in from right
  const rewindSpring = useSpring({
    from: { opacity: 0, transform: 'translateX(150px) scale(0.8)' },
    to: showContent
      ? { opacity: 1, transform: 'translateX(0px) scale(1)' }
      : { opacity: 0, transform: 'translateX(150px) scale(0.8)' },
    delay: showContent && !isCompleted ? 2000 : 0,
    config: { ...config.gentle, tension: 100, friction: 14 },
    immediate: isCompleted,
  });

  // Subtitle words trail animation - slower config for more staggered effect
  const subtitleWords = ['Your', 'Year', 'in', 'Customer', 'Service'];
  const subtitleTrail = useTrail(subtitleWords.length, {
    from: { opacity: 0, transform: 'translateY(30px)' },
    to: showContent
      ? { opacity: 1, transform: 'translateY(0px)' }
      : { opacity: 0, transform: 'translateY(30px)' },
    delay: showContent && !isCompleted ? 3000 : 0,
    trail: 120, // Stagger delay between each word (ms)
    config: { mass: 1.2, tension: 100, friction: 20, clamp: true },
    immediate: isCompleted || (!showContent && phase === 'disco'),
  });

  // Decorative line animation
  const lineSpring = useSpring({
    from: { width: '0%', opacity: 0 },
    to: showContent
      ? { width: '100%', opacity: 1 }
      : { width: '0%', opacity: 0 },
    delay: showContent && !isCompleted ? 4500 : 0,
    config: { tension: 100, friction: 20 },
    immediate: isCompleted,
  });

  // Loading message animation - only shows if still loading when the message phase arrives
  const showLoadingMessage = isLoadingMessagePhase && isLoading;
  const loadingMessageSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: showLoadingMessage
      ? { opacity: 1, transform: 'translateY(0px)' }
      : { opacity: 0, transform: 'translateY(10px)' },
    config: { tension: 200, friction: 20 },
  });

  return (
    <SlideContainer>
      {/* Sparkles overlay - skip when completed */}
      {sparkles.length > 0 && (
        <div className={styles.sparkles}>
          {sparkles.map((sparkle) => (
            <div
              key={sparkle.id}
              className={styles.sparkle}
              style={
                {
                  left: sparkle.left,
                  top: sparkle.top,
                  '--sparkle-size': `${sparkle.size}px`,
                  '--sparkle-delay': `${sparkle.delay}s`,
                  '--sparkle-duration': `${sparkle.duration}s`,
                  '--sparkle-color': sparkle.color,
                } as CSSProperties
              }
            />
          ))}
        </div>
      )}

      {/* 3D Disco ball - visible until greeting phase */}
      {showDisco && (
        <div
          className={`${styles.discoBallsContainer} ${showAvatar ? styles.hidden : ''}`}
        >
          <DiscoBall position="center" isRevealed={false} size={140} />
        </div>
      )}

      {/* Avatar section - appears after disco phase */}
      <animated.div
        className={styles.avatarSection}
        style={avatarContainerSpring}
      >
        <animated.div className={styles.avatarContainer} style={avatarSpring}>
          <div className={styles.avatarGlow} />
          <Avatar user={user} size="40" className={styles.avatar} />
          <div className={styles.avatarRing} />
        </animated.div>

        <animated.p className={styles.greeting} style={greetingSpring}>
          Hi there,{' '}
          <span className={styles.userName}>
            {user.given_name ?? user.name}
          </span>
          !
        </animated.p>
      </animated.div>

      {/* Main content */}
      <div className={`${styles.content} ${showContent ? styles.visible : ''}`}>
        <animated.div className={styles.yearBadge} style={yearBadgeSpring}>
          <span className={styles.yearText}>{rewindConfig.year}</span>
        </animated.div>

        <h1 className={styles.title}>
          <animated.span className={styles.dixa} style={dixaSpring}>
            Dixa
          </animated.span>
          <animated.span className={styles.rewind} style={rewindSpring}>
            Rewind
          </animated.span>
        </h1>

        <animated.div className={styles.decorativeLine} style={lineSpring} />

        <p className={styles.subtitle}>
          {subtitleTrail.map((style, index) => (
            <animated.span
              key={subtitleWords[index]}
              className={styles.subtitleWord}
              style={style}
            >
              {subtitleWords[index]}
            </animated.span>
          ))}
        </p>

        {showLoadingMessage && (
          <animated.p
            className={styles.loadingMessage}
            style={loadingMessageSpring}
            aria-live="polite"
            role="status"
          >
            Still crunching your numbers. Hang tight…
          </animated.p>
        )}
      </div>
    </SlideContainer>
  );
});
